import { useState } from 'react'
import { ExternalLink, FilePen } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { contractNoticeKind, contractNoticeTitle, shouldCheckContract } from './studentContract'
import { useStudentContract } from './useStudentContract'
import { useContractIssueForm } from './useContractIssueForm'
import { ContractIdentityInputs } from './ContractIdentityInputs'

const STILL_PENDING = 'Ainda não recebemos a confirmação da assinatura. Ela pode levar alguns instantes.'

/**
 * R12-02 — faixa persistente no topo do app do aluno com plano pago (o contrato do plano
 * comprado; com dois planos, um de cada vez): gerar o contrato,
 * abrir o link do Autentique em nova aba e conferir depois ("Já assinei"). Nao bloqueia o
 * acesso e nao fecha — some quando o contrato esta assinado.
 */
export function StudentContractNotice() {
  const user = useAuthStore((state) => state.user)
  const query = useStudentContract(shouldCheckContract(user))
  const [checkMessage, setCheckMessage] = useState<string | null>(null)

  const info = query.data?.contract
  const kind = contractNoticeKind(info)
  const form = useContractIssueForm(info, kind !== null && kind !== 'sign')
  if (!kind || !info?.available) return null

  const checkSigned = async () => {
    setCheckMessage(null)
    const result = await query.refetch()
    if (result.data?.contract.status !== 'signed') setCheckMessage(STILL_PENDING)
  }

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-primary-300 bg-primary-50 p-4 text-sm text-primary-900 sm:flex-row sm:items-center dark:border-primary-500/40 dark:bg-primary-500/10 dark:text-primary-100"
    >
      <FilePen className="hidden h-5 w-5 flex-shrink-0 sm:block" aria-hidden />
      <div className="flex-1 space-y-1">
        <p>
          <strong className="font-semibold">{contractNoticeTitle(info)}</strong>{' '}
          {kind === 'rejected'
            ? 'O contrato anterior foi recusado. Gere um novo para assinar.'
            : 'A assinatura é digital e leva poucos minutos.'}
        </p>
        {form.error && <p role="alert" className="text-red-700 dark:text-red-300">{form.error}</p>}
        {checkMessage && <p>{checkMessage}</p>}
      </div>

      {kind === 'sign' && info.sign_url ? (
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={info.sign_url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'primary', size: 'sm' }))}
          >
            Abrir contrato para assinar
            <ExternalLink className="ml-1 h-4 w-4" aria-hidden />
          </a>
          <Button type="button" variant="outline" size="sm" onClick={checkSigned} isLoading={query.isFetching}>
            Já assinei
          </Button>
        </div>
      ) : (
        <form onSubmit={form.submit} className="flex flex-wrap items-center gap-2">
          <ContractIdentityInputs form={form} />
          <Button type="submit" variant="primary" size="sm" isLoading={form.isPending}>
            Gerar contrato
          </Button>
        </form>
      )}
    </div>
  )
}
